import api from '../../api';
import {
	TAG_NAME_CLOCK_SET,
	TAG_NAME_CLOCK_START,
	TAG_NAME_CLOCK_STOP,
	TAG_NAME_CUT,
	TAG_NAME_GOAL,
	TAG_NAME_RUN,
	TAG_TARGET_EVENT,
	TAG_TARGET_PLAYER,
	TAG_TARGET_TEAM,
	TAG_NAME_ACTION_2_MINUTES,
	TAG_NAME_ACTION_5_MINUTES,
	TAG_NAME_HIGHLIGHT,
	TAG_NAME_SCORE_SET,
} from '@videotools/js/api/entities/TagButton';

import { getSportScoreTagName } from '@videotools/js/logic/game/score';

const SPECIAL_TAGS_TYPES = new Set(['special']);

const getInitialState = () => ({
	sorting: {},
});

export default {
	namespaced: true,
	state:      getInitialState,
	mutations:  {
		setSorting(state, { component, key, direction }) {
			state.sorting[component] = {
				key,
				direction,
			};
		},
	},
	actions: {
		setSorting({ commit }, value) {
			commit('setSorting', value);
		},
		likeEventTag: async(_, { tagId, eventId }) => {
			try {
				const res = await api.likeEventTag(tagId, eventId);
				return res && res.status && res.status === 201;
			} catch (error) {
				return false;
			}
		},
	},
	getters: {
		sorting:     (state) => state.sorting,
		tags:        (state, getters, rootState, rootGetters) => rootGetters['tagGroups/tags'],
		tagById:     (state, getters) => (id) => getters.allTags.find(t => t.tag.id === id),
		timingTags:  (state, getters, rootState, rootGetters) => rootGetters['tagGroups/timingTags'],
		allTags:     (state, getters) => [...getters.tags, ...getters.timingTags],
		isForTiming: (state, getters) => (tagType) => {
			return getters.timingTags.map(t => t.tag.id).includes(tagType.id);
		},
		sortedTags: (state, getters) => {
			const tags = Array.from(getters.tags);
			tags.sort((a, b) => (a.timestamp < b.timestamp ? 1 : -1));
			return tags;
		},
		participantTags: (state, getters) => {
			const tags = getters.participantTargetTags;
			tags.sort((a, b) => b.priority - a.priority);
			return tags;
		},
		isForParticipant: (state, getters) => (tagType) => {
			let tag = tagType;
			if (!tag.target) {
				// assume we got an incomplete tag
				tag = getters.tagById(tagType.id);
			}
			return [TAG_TARGET_PLAYER, TAG_TARGET_TEAM].includes(tag?.target);
		},
		specialTags: (state, getters) => getters.tags.filter((tag) => SPECIAL_TAGS_TYPES.has(tag.type)),
		customTags:  (state, getters) => getters.tags.filter((tag) => tag.isCustom),
		matchTags:   (state, getters, rootState, rootGetters) => {
			const tags = getters.tags.filter((tag) => {
				// Match tags are applied to the event, not a player or team.
				// The cut tag is excluded from tag buttons in the UI, because its handled by the cut-in / cut-out
				// highlight tag is excluded becasue it can only be added by automatically
				return tag.isMatchTag() && ![TAG_NAME_CUT, TAG_NAME_HIGHLIGHT, TAG_NAME_SCORE_SET].includes(tag.name);
			});

			// For ultimate_frisbee, include clock tags in match tags (they're normally in SCOREBOARD section)
			const sportName = rootGetters['event/sport']?.name;
			if (sportName === 'ultimate_frisbee') {
				const clockTags = getters.timingTags.filter((tag) =>
					[TAG_NAME_CLOCK_START, TAG_NAME_CLOCK_STOP].includes(tag.name)
				);
				tags.push(...clockTags);
			}

			tags.sort((a, b) => b.priority - a.priority);
			return tags;
		},
		eventTargetTags: (state, getters) => {
			return [
				...getters.tags.filter(tag => tag.target === TAG_TARGET_EVENT),
			];
		},
		isForEvent: (state, getters, rootState, rootGetters) => (tagType) => {
			let tag = tagType;
			if (!tag.target) {
				// assume we got an incomplete tag
				tag = getters.tagById(tagType.id);
			}
			// For ultimate_frisbee, include clock tags as event tags
			const sportName = rootGetters['event/sport']?.name;
			if (sportName === 'ultimate_frisbee' && getters.isForTiming(tag)) {
				const isClockTag = [TAG_NAME_CLOCK_START, TAG_NAME_CLOCK_STOP].includes(tag.name);
				if (isClockTag) {
					return tag.target === TAG_TARGET_EVENT;
				}
			}
			return tag.target === TAG_TARGET_EVENT && !getters.isForTiming(tag);
		},
		participantTargetTags: (state, getters) => {
			return [
				...getters.tags.filter(tag => [TAG_TARGET_PLAYER, TAG_TARGET_TEAM].includes(tag.target)),
			];
		},
		cutTag:            (state, getters) => getters.tags.find((tag) => tag.name === TAG_NAME_CUT && tag.target === TAG_TARGET_EVENT),
		runTag:            (state, getters) => getters.tags.find((tag) => tag.name === TAG_NAME_RUN),
		athleteMarkingTag: (state, getters) => getters.tags.find((tag) => tag.name === TAG_NAME_CUT && tag.target === TAG_TARGET_PLAYER),
		goalTag:           (state, getters) => getters.tags.find((tag) => tag.name === TAG_NAME_GOAL),
		scoreTag:          (state, getters) => (sportName) => getters.tags.find((tag) => {
			return tag.name === getSportScoreTagName(sportName);
		}),
		clockStartTag: (state, getters) => getters.timingTags.find((tag) => tag.name === TAG_NAME_CLOCK_START),
		clockStopTag:  (state, getters) => getters.timingTags.find((tag) => tag.name === TAG_NAME_CLOCK_STOP),
		clockSetTag:   (state, getters) => getters.timingTags.find((tag) => tag.name === TAG_NAME_CLOCK_SET),
		action_2m_tag: (state, getters) => getters.tags.find((tag) => tag.name === TAG_NAME_ACTION_2_MINUTES),
		action_5m_tag: (state, getters) => getters.tags.find((tag) => tag.name === TAG_NAME_ACTION_5_MINUTES),
	},
};
